/**
 * Seller Context Hook
 *
 * Provides access to seller context data (sessionId, customerId, merchantId)
 * from the GetSellerContext API endpoint.
 *
 * This hook fetches seller context on mount, caches it for 5 minutes in Redux, and publishes
 * seller context to Katal Metrics for attribution.
 *
 * Session IDs are persistent (cookie-based) and do not change on login/logout.
 * Only clearing cookies will end a session.
 */

import { useEffect, useRef } from 'react';
import { useGetSellerContextQuery, suApiSlice } from 'src/redux/slices/api/su';
import { GetSellerContextResponse } from 'src/types/api/sellerContext';
import katalMetricsPublisher from 'src/metrics/katalMetricsPublisher';
import { AppState } from 'src/redux/store';

/**
 * Seller context data with convenient accessors
 */
export interface SellerContext {
  sessionId?: string;
  customerId?: string;
  merchantId?: string;
  marketplaceId?: string;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Hook to access seller context data
 *
 * Automatically fetches seller context from the API, caches it, and publishes
 * seller context to Katal Metrics for attribution.
 *
 * Session IDs are persistent and do not expire on login/logout.
 * The customerId and merchantId will update when the user logs in/out,
 * but the sessionId remains constant (tied to browser cookies).
 *
 * @returns Seller context with sessionId, customerId, merchantId, and loading state
 *
 * @example
 * const { sessionId, customerId, merchantId, isLoading } = useSellerContext();
 *
 * if (isLoading) {
 *   return <Loading />;
 * }
 *
 * // Use sessionId for tracking (always present)
 * // Use customerId/merchantId for authenticated user tracking (present when logged in)
 */
export function useSellerContext(): SellerContext {
  const { data, isLoading, isError } = useGetSellerContextQuery();
  const isFirstApiResponse = useRef<boolean>(true);

  // Publish seller context to Katal Metrics once per page load for attribution
  // This links engagement events to seller identity via shared request_id
  useEffect(() => {
    if (data && isFirstApiResponse.current) {
      const {
        sessionId, obfuscatedCustomerId, obfuscatedMerchantId, obfuscatedMarketplaceId,
      } = data;

      // Publish seller context for attribution
      // All engagement events on this page will share the same request_id
      // and can be joined with this seller context in the data pipeline
      try {
        katalMetricsPublisher
          .newChildActionPublisherForMethod('SellerUniversitySellerData')
          .publishString('seller_data', JSON.stringify({
            session: sessionId,
            cid: obfuscatedCustomerId,
            mcid: obfuscatedMerchantId,
            marketplace: obfuscatedMarketplaceId,
          }));
      } catch (error) {
        console.error('Failed to publish seller context to Katal Metrics', error);
      } finally {
        // Always mark as published to prevent retry loops, even if publishing failed
        isFirstApiResponse.current = false;
      }
    }
  }, [data]);

  return {
    sessionId: data?.sessionId,
    customerId: data?.obfuscatedCustomerId,
    merchantId: data?.obfuscatedMerchantId,
    marketplaceId: data?.obfuscatedMarketplaceId,
    isLoading,
    isError,
  };
}

/**
 * Get seller context data synchronously from Redux store
 *
 * This function retrieves cached seller context data without triggering a new API call.
 * Returns undefined if data is not yet loaded.
 *
 * Note: This should only be used when you're certain the data has already been fetched
 * (e.g., after useSellerContext has been called in a parent component).
 *
 * @param state - Redux root state
 * @returns Seller context data or undefined if not loaded
 */
export function getSellerContextFromStore(state: AppState): GetSellerContextResponse | undefined {
  // Use RTK Query's built-in selector for type-safe cache access
  const selectResult = suApiSlice.endpoints.getSellerContext.select(undefined);
  return selectResult(state)?.data;
}
